import type { IDatePicker, IWedding } from '@bridebook/models/source/models/Weddings.types';
import { CountryCodes } from '@bridebook/toolbox/src/gazetteer';
import { extractFormattedLocationName } from '@bridebook/web/lib/weddings/selectors';
import { isGenericWeddingLocation } from './generic-location';
import { formatWeddingDate, weddingDateFromPicker } from './wedding-date';

/**
 * The fields shared by the wedding record and the store's profile, so either one works.
 * `initializedAt` is a timestamp on the record and a number in the store; only its
 * presence is read.
 */
export type WeddingProfileSource = Pick<
  IWedding,
  | 'partners'
  | 'roles'
  | 'date'
  | 'location'
  | 'l10n'
  | 'guests'
  | 'budget'
  | 'planningStage'
  | 'bookings'
> & {
  tasks?: { done?: number; total?: number; initializedAt?: unknown } | null;
};

// The couple's profile as the planner reads it, on the client and in the route alike.
export type WeddingProfileSummary = {
  partnerNames: string[];
  // Same order as the names; '' where a partner has no role set.
  partnerRoles?: string[];
  date: string | null;
  location: string | null;
  searchArea: string | null;
  country: string | null;
  currency: string;
  guestEstimate: number | null;
  guestCount: number;
  budget: number | null;
  // From the wedding record, so every reader agrees on it.
  venueBooked: boolean;
  tasksDone: number;
  tasksTotal: number;
  checklistInitialised: boolean;
  planningStage: {
    hasBudget: boolean;
    hasGuests: boolean;
    hasVenue: boolean;
    hasWeddingDate: boolean;
  };
};

// The profile string carries the whole stored date, season and weekday included.
const formatDate = (date: IDatePicker | undefined): string | null => {
  const parsed = weddingDateFromPicker(date);
  return parsed ? formatWeddingDate(parsed) : null;
};

const formatLocation = (location: IWedding['location'] | null | undefined): string | null => {
  if (!location) {
    return null;
  }
  return [location.name, location.city].filter(Boolean).join(', ') || null;
};

/**
 * The profile derived from the wedding record alone. The route summary and the client use
 * the same derivation, so each field means the same thing everywhere. Null without a
 * wedding.
 */
export const summariseProfile = (
  wedding: WeddingProfileSource | undefined,
): WeddingProfileSummary | null => {
  if (!wedding) {
    return null;
  }
  // Every account starts with its location set to the market's country name
  const genericLocation = isGenericWeddingLocation(wedding.location);

  return {
    partnerNames: wedding.partners,
    partnerRoles: wedding.roles ?? [],
    date: formatDate(wedding.date),
    location: genericLocation ? null : formatLocation(wedding.location),
    searchArea: genericLocation
      ? null
      : (extractFormattedLocationName(wedding.location, wedding.l10n.country ?? CountryCodes.GB) ??
        wedding.location?.city ??
        null),
    country: wedding.l10n.country ?? null,
    currency: wedding.l10n.currency ?? 'GBP',
    guestEstimate: wedding.guests?.estimate ?? null,
    guestCount: wedding.guests?.count ?? 0,
    budget: wedding.budget ?? null,
    venueBooked: Boolean(wedding.bookings?.venue),
    tasksDone: wedding.tasks?.done ?? 0,
    tasksTotal: wedding.tasks?.total ?? 0,
    checklistInitialised: Boolean(wedding.tasks?.initializedAt),
    planningStage: {
      hasBudget: wedding.planningStage?.hasBudget ?? false,
      hasGuests: wedding.planningStage?.hasGuests ?? false,
      hasVenue: wedding.planningStage?.hasVenue ?? false,
      hasWeddingDate: wedding.planningStage?.hasWeddingDate ?? false,
    },
  };
};
